import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { AddMiniIcon, AiSparkleIcon } from '@calendly/ui/components/icons'
import { createContext, useContext } from 'react'

import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import {
  useAddTrackedMeetingMutation,
  useAddTrackedExternalMeetingMutation,
  useFetchTrackedMeetingByForeignIdQuery
} from 'hooks/tracked_meetings/api'
import { isCalendlyMeeting } from 'shared/utils'

import { isPendingEvent } from '../utils'

const NotetakerContext = createContext({
  notetakerAdded: false,
  canAdd: false,
  hasErrors: false,
  isLoading: false,
})

const useCanAddNotetaker = (event) => {
  const miRecordingRulesEnabled = useFlag('meeting_intelligence')
  const { data: meetingIntelligenceSettings, isLoading: isLoadingSettings } = useGetMeetingIntelligenceUserSettingsQuery(
    {},
    { skip: !miRecordingRulesEnabled }
  )
  const { data: meetingIntelligence, isFetching: isLoadingData } = useFetchTrackedMeetingByForeignIdQuery(
    event?.uuid,
    { skip: !event || !miRecordingRulesEnabled || isPendingEvent(event) }
  )

  if (!miRecordingRulesEnabled) {
    return { notetakerAdded: false, canAdd: false, hasErrors: false, isLoading: false }
  }

  const canAdd = !isPendingEvent(event)
    && !!meetingIntelligenceSettings?.miEnabled
    && event.policy?.addAiNotetaker

  const hasErrors = Boolean(meetingIntelligence?.trackedDataError)

  return {
    notetakerAdded: meetingIntelligence && meetingIntelligence?.enabled,
    canAdd,
    hasErrors,
    isLoading: isLoadingSettings || isLoadingData,
  }
}

export const NotetakerProvider = ({ event, children }) => {
  const value = useCanAddNotetaker(event)

  return (
    <NotetakerContext.Provider value={value}>
      {children}
    </NotetakerContext.Provider>
  )
}

export const useNotetaker = () => {
  const value = useContext(NotetakerContext)

  if (!value) {
    return {}
  }

  return value
}

const MeetRecapsVariant = () => (
  <>
    <AiSparkleIcon />
    {I18n.t('publisher.meeting_intelligence.scheduled_event.try_free')}
  </>
)

const StandardVariant = ({ isBannerButton }) => (
  <>
    <AddMiniIcon spacingRight={isBannerButton ? 4 : 0} />
    {I18n.t('publisher.meeting_intelligence.scheduled_event.add')}
  </>
)

const AddNotetakerButtonComponent = ({ event, buttonLocation, text }) => {
  const track = useTrack()
  const { canAdd, hasErrors, notetakerAdded } = useNotetaker()
  const isBannerButton = buttonLocation === 'notetaker_banner'
  const [addTrackedMeeting, addTrackedMeetingState] = useAddTrackedMeetingMutation()
  const [addExternalTrackedMeeting, addExternalTrackedMeetingState] = useAddTrackedExternalMeetingMutation()

  const trackedMeetingMutation = (event.kind && event.kind !== 'CALENDLY')
    ? { state: addExternalTrackedMeetingState, fn: addExternalTrackedMeeting }
    : { state: addTrackedMeetingState, fn: addTrackedMeeting }

  const onClick = () => {
    trackedMeetingMutation.fn({ eventId: event.id, eventUuid: event.uuid, event })
    track({
      name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
      properties: {
        action: 'enable_notetaker',
        location: isBannerButton ? 'meetings_page_right_rail_banner' : 'meetings_page_right_rail',
        is_calendly_meeting: isCalendlyMeeting(event),
        event_uuid: event.uuid,
        mi_event_uuid: event.meetingIntelligence?.uuid,
      }
    })
  }
  if (!canAdd || notetakerAdded) {
    return null
  }

  if (hasErrors) {
    return (
      <Button size="small" disabled>
        {I18n.t('publisher.meeting_intelligence.scheduled_event.unavailable')}
      </Button>
    )
  }

  return (
    <Button
      size={isBannerButton ? 'normal' : 'small'}
      loading={trackedMeetingMutation.state.isLoading}
      onClick={onClick}
      aria-label="add-notetaker-button"
    >
      {text ? (<MeetRecapsVariant />) : (<StandardVariant isBannerButton={isBannerButton} />)}
    </Button>
  )
}

export const AddNotetakerButton = ({ event, buttonLocation, text = null, isNotPretending }) => {
  if (!isNotPretending) return null

  return (
    <AddNotetakerButtonComponent
      event={event}
      buttonLocation={buttonLocation}
      text={text}
    />
  )
}
